import type { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import axios, { AxiosHeaders } from 'axios';
import { ElMessage } from 'element-plus';
import { ROUTER_WHITE_LIST } from '@/config';
import { isRoutePatternMatch } from '@/config/domainRouteAccess';
import router from '@/routers';
import { useUserStore } from '@/stores';

export interface BaseResponse<T = any> {
  code: number;
  data: T;
  msg: string;
}

const EMPTY_RESPONSE_STATUS = new Set([204, 205]);
const fpPromise = FingerprintJS.load();

function isCurrentPageWhitelisted() {
  return ROUTER_WHITE_LIST.some(p => isRoutePatternMatch(p, window.location.pathname));
}

async function getFingerprint(): Promise<string> {
  const fp = await fpPromise;
  const { visitorId } = await fp.get();
  return visitorId;
}

function isSuccessCode(code?: number) {
  return typeof code === 'number' && code >= 200 && code < 300;
}

function toSuccessResponse<T = any>(data: T = null as T): BaseResponse<T> {
  return {
    code: 200,
    data,
    msg: 'success',
  };
}

export function normalizeResponseData<T = any>(status: number, data: any): BaseResponse<T> {
  if (EMPTY_RESPONSE_STATUS.has(status) || data === '' || data == null) {
    return toSuccessResponse<T>();
  }

  if (typeof data?.code === 'number') {
    return data;
  }

  return toSuccessResponse<T>(data);
}

export async function applyAuthHeaders(headers: Headers | AxiosHeaders | Record<string, any>) {
  const userStore = useUserStore();
  const setHeader = (key: string, value: string) => {
    if (headers instanceof Headers) {
      headers.set(key, value);
    }
    else if (headers instanceof AxiosHeaders) {
      headers.set(key, value);
    }
    else {
      headers[key] = value;
    }
  };

  try {
    setHeader('X-Fingerprint', await getFingerprint());
  }
  catch (error) {
    console.error('Failed to generate fingerprint:', error);
  }

  if (userStore.refreshToken) {
    setHeader('refresh_token', `${userStore.refreshToken}`);
  }
  if (userStore.token) {
    setHeader('authorization', `Bearer ${userStore.token}`);
  }
}

export function syncTokenFromHeaders(headers: Headers | AxiosHeaders | Record<string, any>) {
  const userStore = useUserStore();
  const getHeader = (key: string) => {
    if (headers instanceof Headers) {
      return headers.get(key);
    }
    if (headers instanceof AxiosHeaders) {
      return headers.get(key);
    }
    return headers[key] ?? headers[key.toLowerCase()];
  };

  const accessToken = getHeader('access_token');
  if (accessToken) {
    userStore.setToken(accessToken, getHeader('refresh_token'));
  }
}

function handleUnauthorized() {
  if (isCurrentPageWhitelisted()) {
    return;
  }

  const userStore = useUserStore();
  ElMessage.error('登录已过期，请重新登录！');
  userStore.logout();
  router.replace('/login');
}

function rejectBusinessResponse(response: BaseResponse) {
  if (response.code === 403) {
    router.replace({ name: '403' });
    ElMessage.error({
      message: response.msg,
      duration: 3000,
    });
    return Promise.reject(response);
  }

  if (response.code === 401) {
    handleUnauthorized();
    return Promise.reject(response);
  }

  ElMessage.error(response.msg);
  return Promise.reject(response);
}

function rejectHttpError(error: AxiosError) {
  const userStore = useUserStore();
  const status = error.response?.status;
  const data: any = error.response?.data;

  if (status === 403) {
    const msg = data?.error?.message ?? data?.msg ?? data?.message;
    if (data?.error?.code === '422') {
      return Promise.reject(data.error);
    }

    if (msg) {
      ElMessage.error(msg);
      return Promise.reject(data?.error ?? error);
    }

    return Promise.reject(new Error('无权限'));
  }

  if (status === 401) {
    handleUnauthorized();
    return Promise.reject(error);
  }

  if (status) {
    ElMessage.error(data?.msg ?? data?.message ?? error.message);
    return Promise.reject(error);
  }

  if (!userStore.token) {
    return Promise.reject(error);
  }

  ElMessage.error(error.message || '网络请求失败');
  return Promise.reject(error);
}

export const request = axios.create({
  baseURL: import.meta.env.VITE_WEB_BASE_API,
  headers: {
    'Content-Type': 'application/json',
  },
});

request.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  config.headers = AxiosHeaders.from(config.headers);
  await applyAuthHeaders(config.headers);
  return config;
});

request.interceptors.response.use(
  (response) => {
    syncTokenFromHeaders(response.headers);
    const result = normalizeResponseData(response.status, response.data);

    if (isSuccessCode(result.code)) {
      return result;
    }

    return rejectBusinessResponse(result);
  },
  error => rejectHttpError(error),
);

export function post<T = any, D = any>(url: string, data?: D, config?: AxiosRequestConfig) {
  return request.post<any, BaseResponse<T>, D>(url, data, config);
}

export function get<T = any, P = any>(url: string, params?: P, config?: AxiosRequestConfig) {
  return request.get<any, BaseResponse<T>>(url, { ...config, params });
}

export function put<T = any, D = any>(url: string, data?: D, config?: AxiosRequestConfig) {
  return request.put<any, BaseResponse<T>, D>(url, data, config);
}

export function del<T = any>(url: string, config?: AxiosRequestConfig) {
  return request.delete<any, BaseResponse<T>>(url, config);
}

export default request;
